import React, {memo, useRef} from 'react'
import {Breadcrumb, Button, Dropdown, Grid, Menu, Ref, Sticky} from 'semantic-ui-react'
import {
  atServiceLimit, coerceToISODate,
  getFirstService,
  getNameOptions,
  getPublishedOrFirstVersion,
  getVisibleServices,
  goTo
} from '../../util'
import {withChildPathProps, withMeData, withNavigation, withPropsPresent, withServicesData} from '../../hoc'
import {useSafeState} from '../../hook'
import {orderBy} from 'lodash'

const withNavRef = (navRefs, serviceId) => {
  let {pathname, search} = window.location
  if (search) {
    pathname = `${pathname}?${search}`
  }
  navRefs[serviceId] = pathname
  return navRefs
}

const _Services = (
  {
    me,
    services = [],
    serviceId,
    navigate,
    goTo,
    basePath = '/c/services',
    children
  }
) => {
  const servicesRef = useRef()
  const [state, setState] = useSafeState({selectedNames: [], navRefs: {}})

  const onAdd = (e) => {
    e.preventDefault()
    navigate(`${basePath}/add`)
  }

  const onUpload = (e) => {
    e.preventDefault()
    navigate(`${basePath}/upload-contract`)
  }

  const onSelect = (service) => {
    const version = getPublishedOrFirstVersion(service)
    const target = !version ? `${basePath}/${service.id}/versions` : `${basePath}/${service.id}/versions/${version.id}/environments`
    const destination = state.navRefs[service.id] || target
    setState({...state, navRefs: withNavRef(state.navRefs, serviceId)})
    navigate(destination)
  }

  const onFilterByNames = (e, d) => {
    setState({...state, selectedNames: d.value})
    const visibleServices = getVisibleServices(d.value, services)
    let selectedService = visibleServices.find(s => s.name === serviceId)
    if (!selectedService) {
      selectedService = getFirstService(visibleServices)
    }
    navigate(`/c/services/${selectedService.id}/versions`)
  }

  const nameOptions = getNameOptions(orderBy(services.map((service) => {
    service.lastAccessedAt = !!service.lastAccessedAt ? coerceToISODate(service.lastAccessedAt) : new Date(1901,1,1)
    return service
  }), ['lastAccessedAt', 'asc']))

  const visibleServices = getVisibleServices(state.selectedNames, services)

  return (
    <Ref innerRef={servicesRef}>
      <Grid divided='vertically'>
        <Grid.Row coloumns='16'>
          <Grid.Column width='16'>
            <Breadcrumb>
              <Breadcrumb.Section link
                                  onClick={() => goTo(`${process.env.REACT_APP_SITE_URL}`)}>Home</Breadcrumb.Section>
              <Breadcrumb.Divider/>
              <Breadcrumb.Section link
                                  onClick={() => navigate('/c/services')}>Console</Breadcrumb.Section>
              <Breadcrumb.Divider/>
              <Breadcrumb.Section active>Mocks</Breadcrumb.Section>
            </Breadcrumb>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row columns='16'>
          <Grid.Column width='16'>
            <Dropdown placeholder='Filter by service name(s)' fluid multiple search selection
                      disabled={nameOptions.length === 0}
                      options={nameOptions}
                      value={state.selectedNames}
                      clearable
                      closeOnChange={false}
                      data-testid='services-filter-dropdown'
                      onChange={onFilterByNames}/>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row columns='16'>
          <Grid.Column width='4'>
            <Button.Group size='tiny'>
              <Button onClick={(e) => onAdd(e)} icon='add'
                      primary
                      data-testid='top-add-service-button'
                      content='Add Service' disabled={atServiceLimit(me)}/>
              <Button.Or/>
              <Button onClick={(e) => onUpload(e)} content='Upload Contract' disabled={atServiceLimit(me)}
                      className='upload-service-button'
                      data-testid='top-upload-contract-button'
                      icon='upload'/>
            </Button.Group>
            {
              visibleServices.length > 0 && (
                <Sticky context={servicesRef} offset={65}>
                  <div style={{overflowY: 'auto', maxHeight: window.screen.availHeight - 450, margin: '25px 0px'}}>
                    <Menu fluid vertical tabular data-testid='services-left-menu'>
                      {
                        visibleServices.map((s) => (
                            <Menu.Item key={s.id}
                                       content={s.name}
                                       active={s.id === serviceId}
                                       data-test='services-left-menu-item'
                                       onClick={() => onSelect(s)}
                            />
                          )
                        )
                      }
                    </Menu>
                  </div>
                </Sticky>
              )
            }
            {
              visibleServices.length === 0 && (
                <div data-testid='services-left-menu-not-found' style={{marginTop: '25px'}}>No services found</div>
              )
            }
          </Grid.Column>
          <Grid.Column width='12'>
            <Grid>
              <Grid.Row columns='16'>
                <Grid.Column width='16'>
                  {children}
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Ref>
  )
}

let Services = (props) => {
  const path = props.location.pathname
  if (path.endsWith('/services')) {
    const {services = []} = props
    const serviceId = props.serviceId || (getFirstService(services) || {}).id
    if (!!serviceId) {
      props.navigate(`${serviceId}/versions`, {forwardQueryParams: ['returnPath']})
      return null
    }
  }


  props = {...props, goTo}
  return (
    <_Services {...props}/>
  )
}

Services = withPropsPresent(Services, ['services', 'servicesLoaded', 'me'])
Services = withServicesData(Services, 'Loading services...')
Services = withMeData(Services, 'Loading profile...')
Services = withNavigation(Services)
Services = withChildPathProps(Services)
Services = memo(Services)

export {_Services, Services}

export default Services